import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";

interface UseInlineEditOptions {
  onSave: (id: string, value: string) => void;
}

export type UseInlineEditReturn = ReturnType<typeof useInlineEdit>;

export const useInlineEdit = ({ onSave }: UseInlineEditOptions) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = useCallback((id: string, currentValue: string) => {
    setEditingId(id);
    setEditingValue(currentValue);
  }, []);

  const saveEdit = useCallback(() => {
    const trimmed = editingValue.trim();
    if (trimmed && editingId) {
      onSave(editingId, trimmed);
    }
    setEditingId(null);
    setEditingValue("");
  }, [editingValue, editingId, onSave]);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingValue("");
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        saveEdit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelEdit();
      }
    },
    [saveEdit, cancelEdit]
  );

  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  return {
    editingId,
    editingValue,
    setEditingValue,
    inputRef,
    startEdit,
    saveEdit,
    cancelEdit,
    handleKeyDown,
    isEditing: (id: string) => editingId === id,
  };
};
